import type {ReactNode} from 'react'
import {createContext, use, useCallback, useMemo, useState} from 'react'

import type {CopilotSpaceId} from '../utils/copilot-chat-types'

interface CopilotSpaceSelectionContextValue {
  /** The currently selected copilot space */
  selectedCopilotSpaceId: CopilotSpaceId | undefined
  /** Function to update the selected copilot space */
  setSelectedCopilotSpaceId: (id: CopilotSpaceId | undefined) => void
  /** Selected thread from the chat state */
  selectedThreadId: string | null
}

const CopilotSpaceSelectionContext = createContext<CopilotSpaceSelectionContextValue | null>(null)

interface CopilotSpaceSelectionProviderProps {
  children: ReactNode
  selectedThreadId: string | null
}

export function CopilotSpaceSelectionProvider({selectedThreadId, children}: CopilotSpaceSelectionProviderProps) {
  const [selectedCopilotSpaceId, setSelectedCopilotSpaceId] = useState<CopilotSpaceId | undefined>()

  const updateSelectedCopilotSpaceId = useCallback((id: CopilotSpaceId | undefined) => {
    setSelectedCopilotSpaceId(id)
  }, [])

  const value: CopilotSpaceSelectionContextValue = useMemo(
    () => ({
      selectedCopilotSpaceId,
      setSelectedCopilotSpaceId: updateSelectedCopilotSpaceId,
      selectedThreadId,
    }),
    [selectedCopilotSpaceId, updateSelectedCopilotSpaceId, selectedThreadId],
  )

  return <CopilotSpaceSelectionContext value={value}>{children}</CopilotSpaceSelectionContext>
}

/**
 * Hook to access and modify copilot space selection state, intended
 * for consumption by useSpaceSelection
 * @returns The copilot space selection context value
 * @throws Error if used outside of CopilotSpaceSelectionProvider
 */
export function useCopilotSpaceSelection(): CopilotSpaceSelectionContextValue {
  const context = use(CopilotSpaceSelectionContext)
  if (!context) {
    throw new Error('useCopilotSpaceSelection must be used within a CopilotSpaceSelectionProvider')
  }
  return context
}
